import UserService from "@/services/api/user";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";

interface LoadingState {
  getWorkStation: boolean;
  getUserById: boolean;
  getRolesList: boolean;
  getManagers: boolean;
  getDesignationList: boolean;
  getDepartmentList: boolean;
  getBranchList: boolean;
  getUsersData: boolean;
  getQualificationList: boolean;
  getUserExport: boolean;
  getUsersBulkUploadFiles: boolean;
  getUsersBulkUploadReport: boolean;
  getReportDownloadFile: boolean;
  getUsersBulkUploadViewReport: boolean;
  deleteInactiveUser: boolean;
  updateUsersBulkUpload: boolean;
  viewUser: boolean;
  myTeamList: boolean;
  workingLocation: boolean;
  updateLoading: boolean;
  uploadImageLoading: boolean;
  getAuditLog: boolean;
  getAuditLogDetails: boolean;
}

interface UserState {
  users: any[];
  setting: any[];
  userDetails: any[];
  workStationList: any[];
  globalSetting: any[];
  designationList: any[];
  departmentList: any[];
  managerList: any[];
  branchList: any[];
  roleList: any[];
  myTeamList: any[];
  qualificationList: any[];
  userReportList: any[];
  workingLocation: any[];
  userBulkReportViewData: any;
  auditLogList: { Items: any[]; TotalRecords: number } | null;
  auditLogDetailsList: any[];
  loading: LoadingState;
  error: string | null;
}

const initialState: UserState = {
  users: [],
  setting: [],
  userDetails: [],
  workStationList: [],
  globalSetting: [],
  designationList: [],
  departmentList: [],
  managerList: [],
  branchList: [],
  roleList: [],
  qualificationList: [],
  userReportList: [],
  myTeamList: [],
  workingLocation: [],
  userBulkReportViewData: null,
  auditLogList: null,
  auditLogDetailsList: [],
  loading: {
    getWorkStation: false,
    getUserById: false,
    getRolesList: false,
    getManagers: false,
    getDesignationList: false,
    getDepartmentList: false,
    getBranchList: false,
    getUsersData: false,
    getQualificationList: false,
    getUserExport: false,
    getUsersBulkUploadFiles: false,
    getUsersBulkUploadReport: false,
    getReportDownloadFile: false,
    getUsersBulkUploadViewReport: false,
    deleteInactiveUser: false,
    updateUsersBulkUpload: false,
    viewUser: false,
    myTeamList: false,
    workingLocation: false,
    updateLoading: false,
    uploadImageLoading: false,
    getAuditLog: false,
    getAuditLogDetails: false,
  },
  error: null,
};

// Async thunk
export const getWorkStation = createAsyncThunk(
  "user/WorkStation",
  async (_, { rejectWithValue }) => {
    try {
      const response = await UserService.getWorkStations();
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
export const getUserById = createAsyncThunk(
  "user/user",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await UserService.getUserById(id);
      return [response.data];
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const viewUser = createAsyncThunk(
  "user/viewUser",
  async (id: any, { rejectWithValue }) => {
    try {
      const response = await UserService.viewUser(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getRolesList = createAsyncThunk(
  "user/roles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await UserService.getRolesList();
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getManagers = createAsyncThunk(
  "user/manager",
  async (_, { rejectWithValue }) => {
    try {
      const res = await UserService.getManagers();
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch managers");
    }
  },
);

export const getDesignationList = createAsyncThunk(
  "user/getDesignationList",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await UserService.getDesignationList(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch designation");
    }
  }
);

export const getDepartmentList = createAsyncThunk(
  "user/getDepartmentList",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await UserService.getDepartmentList(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch department");
    }
  },
);

export const getBranchList = createAsyncThunk(
  "user/getBranchList",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await UserService.getBranchList(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch branch");
    }
  },
);

export const getUsersData = createAsyncThunk(
  "user/getUsersData",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await UserService.getUsersData(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch users");
    }
  },
);

export const getAuditLog = createAsyncThunk(
  "user/getAuditLog",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await UserService.getAuditLog(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch audit log");
    }
  },
);

export const getAuditLogDetails = createAsyncThunk(
  "user/getAuditLogDetails",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await UserService.getAuditLogDetails(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch audit log details");
    }
  },
);

export const getQualificationList = createAsyncThunk(
  "user/getQualificationList",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await UserService.getQualificationList(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch qualification");
    }
  },
);

export const getUserExport = createAsyncThunk(
  "user/getUserExport",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await UserService.getUserExport(data);
      return saveAsExcelFile(response, "Users");
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to export",
      );
    }
  },
);

export const updateUsersBulkUpload = createAsyncThunk(
  "user/updateUsersBulkUpload",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await UserService.updateUsersBulkUpload(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to update users bulk upload",
      );
    }
  },
);

export const getUsersBulkUploadFiles = createAsyncThunk(
  "user/getUsersBulkUploadFiles",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await UserService.getUsersBulkUploadFiles(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch user bulk upload files",
      );
    }
  },
);

export const getUsersBulkUploadReport = createAsyncThunk(
  "user/getUsersBulkUploadReport",
  async (fileId: number, { rejectWithValue }) => {
    try {
      const response = await UserService.exportUsersBulkUploadReport(fileId);
      return saveAsExcelFile(response, "USER_REPORT");
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch user bulk upload report",
      );
    }
  },
);

export const getReportDownloadFile = createAsyncThunk(
  "user/getReportDownloadFile",
  async (fileId: number, { rejectWithValue }) => {
    try {
      const response = await UserService.exportUsersBulkUploadFile(fileId);
      return saveAsExcelFile(response, "USER_FILE");
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to export file",
      );
    }
  },
);

export const getUsersBulkUploadViewReport = createAsyncThunk(
  "user/getUsersBulkUploadViewReport",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await UserService.getUsersBulkUploadViewReport(payload);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch Users Bulk Report View");
    }
  }
);

export const deleteInactiveUser = createAsyncThunk(
  "user/deleteInactiveUser",
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await UserService.deleteInactiveUser(userId);
      return handleResponse(response, rejectWithValue);
    }
    catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to delete user",
      );
    }
  }
);

export const getMyTeamList = createAsyncThunk(
  "user/getMyTeamList",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await UserService.getMyTeamList(payload);
      return handleResponse(response, rejectWithValue);
    }
    catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch my team list",
      );
    }
  }
);

export const getWorkingLocation = createAsyncThunk(
  "user/getWorkingLocation",
  async (userId: number, { rejectWithValue }) => {
    try {
      const res = await UserService.getWorkingLocation(userId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch working locations");
    }
  },
);

export const updateWorkingLocation = createAsyncThunk(
  "user/updateWorkingLocation",
  async (payload: { data: any, userId: number }, { rejectWithValue }) => {
    try {
      const res = await UserService.updateWorkingLocation(payload?.data, payload?.userId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to update working location");
    }
  },
);

export const updateBasicInfo = createAsyncThunk(
  "user/updateBasicInfo",
  async (payload: { userId: number; data: any }, { rejectWithValue }) => {
    try {
      const res = await UserService.basicInfo(payload.data, payload.userId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update basic info");
    }
  }
);

export const updateCurrentPosition = createAsyncThunk("user/updateCurrentPosition",
  async (payload: { userId: number; data: any }, { rejectWithValue }) => {
    try {
      const res = await UserService.currentPosition(payload.data, payload.userId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update current position");
    }
  }
);

export const updatePersonalInfo = createAsyncThunk(
  "user/updatePersonalInfo",
  async (payload: { userId: number; data: any }, { rejectWithValue }) => {
    try {
      const res = await UserService.personalInfo(payload.data, payload.userId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update personal info");
    }
  }
);

export const uploadUserImage = createAsyncThunk(
  "user/uploadUserImage",
  async (payload: { userId: number; data: FormData; }, { rejectWithValue }) => {
    try {
      const res = await UserService.uploadImage(payload.data, payload.userId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to upload image");
    }
  }
);

export const deleteUserImage = createAsyncThunk(
  "user/deleteUserImage",
  async (userId: number, { rejectWithValue }) => {
    try {
      const res = await UserService.deleteUploadImage(userId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to delete image");
    }
  }
);

export const updateStatutoryInfo = createAsyncThunk(
  "user/updateStatutoryInfo",
  async (payload: { userId: number; data: any }, { rejectWithValue }) => {
    try {
      const res = await UserService.statutoryInfo(payload.data, payload.userId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update statutory info");
    }
  }
);

export const updateBankInfo = createAsyncThunk(
  "user/updateBankInfo",
  async (payload: { userId: number; data: any }, { rejectWithValue }) => {
    try {
      const res = await UserService.bankInfo(payload.data, payload.userId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update bank info");
    }
  }
);

export const updateBackgroundCheck = createAsyncThunk(
  "user/updateBackgroundCheck",
  async (payload: { userId: number; data: any }, { rejectWithValue }) => {
    try {
      const res = await UserService.backgroundCheckInfo(payload.data, payload.userId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update background check");
    }
  }
);

export const updateJoiningInfo = createAsyncThunk(
  "user/updateJoiningInfo",
  async (payload: { userId: number; data: any }, { rejectWithValue }) => {
    try {
      const res = await UserService.joiningInfo(payload.data, payload.userId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update joining info");
    }
  }
);

export const updatePresentAddress = createAsyncThunk(
  "user/updatePresentAddress",
  async (payload: { userId: number; data: any }, { rejectWithValue }) => {
    try {
      const res = await UserService.presentAddress(payload.data, payload.userId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update present address");
    }
  }
);

export const updateEmergencyContact = createAsyncThunk(
  "user/updateEmergencyContact",
  async (payload: { userId: number; data: any }, { rejectWithValue }) => {
    try {
      const res = await UserService.emergencyContact(payload.data, payload.userId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update emergency contact");
    }
  }
);

export const updateEducationDetails = createAsyncThunk(
  "user/updateEducationDetails",
  async (payload: { userId: number; data: any }, { rejectWithValue }) => {
    try {
      const res = await UserService.educationDetails(payload.data, payload.userId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update education details");
    }
  }
);

export const updateEmployeeIdentity = createAsyncThunk(
  "user/updateEmployeeIdentity",
  async (payload: { userId: number; data: any }, { rejectWithValue }) => {
    try {
      const res = await UserService.employeeIdentity(payload.data, payload.userId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update employee identity");
    }
  }
);

export const deleteEducationDetails = createAsyncThunk(
  "user/deleteEducationDetails",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await UserService.deleteEducationDetails(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to delete education detail");
    }
  }
);

export const deleteEmergencyContact = createAsyncThunk(
  "user/deleteEmergencyContact",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await UserService.deleteEmergencyContact(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to delete emergency contact");
    }
  }
);

export const deleteEmployeeIdentity = createAsyncThunk(
  "user/deleteEmployeeIdentity",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await UserService.deleteEmployeeIdentity(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to delete employee identity");
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getWorkStation.pending, (state) => {
      state.loading.getWorkStation = true;
      state.error = null;
    });
    builder.addCase(getWorkStation.fulfilled, (state, action) => {
      state.loading.getWorkStation = false;
      state.workStationList = action.payload;
    });
    builder.addCase(getWorkStation.rejected, (state, action: any) => {
      state.loading.getWorkStation = false;
      state.error = action.payload || "Failed to fetch workstation";
    });
    builder.addCase(getDesignationList.pending, (state) => {
      state.loading.getDesignationList = true;
      state.error = null;
    });
    builder.addCase(getDesignationList.fulfilled, (state, action) => {
      state.loading.getDesignationList = false;
      state.designationList = action.payload || [];
    });
    builder.addCase(getDesignationList.rejected, (state, action: any) => {
      state.loading.getDesignationList = false;
      state.error = action.payload || "Failed to fetch designation";
    });
    builder.addCase(getDepartmentList.pending, (state) => {
      state.loading.getDepartmentList = true;
      state.error = null;
    });
    builder.addCase(getDepartmentList.fulfilled, (state, action) => {
      state.loading.getDepartmentList = false;
      state.departmentList = action.payload || [];
    });
    builder.addCase(getDepartmentList.rejected, (state, action: any) => {
      state.loading.getDepartmentList = false;
      state.error = action.payload || "Failed to fetch department";
    });
    builder.addCase(getBranchList.pending, (state) => {
      state.loading.getBranchList = true;
      state.error = null;
    });
    builder.addCase(getBranchList.fulfilled, (state, action) => {
      state.loading.getBranchList = false;
      state.branchList = action.payload || [];
    });
    builder.addCase(getBranchList.rejected, (state, action: any) => {
      state.loading.getBranchList = false;
      state.error = action.payload || "Failed to fetch branch";
    });
    builder.addCase(getRolesList.pending, (state) => {
      state.loading.getRolesList = true;
      state.error = null;
    });
    builder.addCase(getRolesList.fulfilled, (state, action) => {
      state.loading.getRolesList = false;
      state.roleList = action.payload || [];
    });
    builder.addCase(getRolesList.rejected, (state, action: any) => {
      state.loading.getRolesList = false;
      state.error = action.payload || "Failed to fetch roles";
    });
    builder.addCase(getManagers.pending, (state) => {
      state.loading.getManagers = true;
      state.error = null;
    });
    builder.addCase(getManagers.fulfilled, (state, action) => {
      state.loading.getManagers = false;
      state.managerList = action.payload || [];
    });
    builder.addCase(getManagers.rejected, (state, action: any) => {
      state.loading.getManagers = false;
      state.error = action.payload || "Failed to fetch managers";
    });
    builder.addCase(getQualificationList.pending, (state) => {
      state.loading.getQualificationList = true;
      state.error = null;
    });
    builder.addCase(getQualificationList.fulfilled, (state, action) => {
      state.loading.getQualificationList = false;
      state.qualificationList = action.payload || [];
    });
    builder.addCase(getQualificationList.rejected, (state, action: any) => {
      state.loading.getQualificationList = false;
      state.error = action.payload || "Failed to fetch qualification";
    });
    builder.addCase(getUsersData.pending, (state) => {
      state.loading.getUsersData = true;
      state.error = null;
    });
    builder.addCase(getUsersData.fulfilled, (state, action) => {
      state.loading.getUsersData = false;
      state.users = action.payload || [];
    });
    builder.addCase(getUsersData.rejected, (state, action: any) => {
      state.loading.getUsersData = false;
      state.error = action.payload || "Failed to fetch users";
    });
    builder.addCase(getAuditLog.pending, (state) => {
      state.loading.getAuditLog = true;
      state.error = null;
    });
    builder.addCase(getAuditLog.fulfilled, (state, action) => {
      state.loading.getAuditLog = false;
      state.auditLogList = action.payload || { Items: [], TotalRecords: 0 };
    });
    builder.addCase(getAuditLog.rejected, (state, action: any) => {
      state.loading.getAuditLog = false;
      state.error = action.payload || "Failed to fetch audit log";
    });
    builder.addCase(getAuditLogDetails.pending, (state) => {
      state.loading.getAuditLogDetails = true;
      state.error = null;
    });
    builder.addCase(getAuditLogDetails.fulfilled, (state, action) => {
      state.loading.getAuditLogDetails = false;
      state.auditLogDetailsList = action.payload || [];
    });
    builder.addCase(getAuditLogDetails.rejected, (state, action: any) => {
      state.loading.getAuditLogDetails = false;
      state.error = action.payload || "Failed to fetch audit log details";
    });
    builder.addCase(getUserById.pending, (state) => {
      state.loading.getUserById = true;
      state.error = null;
      state.userDetails = [];
    });
    builder.addCase(getUserById.fulfilled, (state, action) => {
      state.loading.getUserById = false;
      state.userDetails = action.payload;
    });
    builder.addCase(getUserById.rejected, (state, action: any) => {
      state.loading.getUserById = false;
      state.error = action.payload || "Failed to fetch user by id";
    });
    builder.addCase(getUserExport.pending, (state) => {
      state.loading.getUserExport = true;
      state.error = null;
    });
    builder.addCase(getUserExport.fulfilled, (state) => {
      state.loading.getUserExport = false;
    });
    builder.addCase(getUserExport.rejected, (state, action: any) => {
      state.loading.getUserExport = false;
      state.error = action.payload || "Failed to export users";
    });
    builder.addCase(getUsersBulkUploadFiles.pending, (state) => {
      state.loading.getUsersBulkUploadFiles = true;
      state.error = null;
      state.userReportList = [];
    });
    builder.addCase(getUsersBulkUploadFiles.fulfilled, (state, action) => {
      state.loading.getUsersBulkUploadFiles = false;
      state.userReportList = action.payload || [];
    });
    builder.addCase(getUsersBulkUploadFiles.rejected, (state, action: any) => {
      state.loading.getUsersBulkUploadFiles = false;
      state.error = action.payload || "Failed to fetch user bulk upload files";
    });
    builder.addCase(getUsersBulkUploadViewReport.pending, (state) => {
      state.loading.getUsersBulkUploadViewReport = true;
      state.error = null;
      state.userBulkReportViewData = null;
    });
    builder.addCase(getUsersBulkUploadViewReport.fulfilled, (state, action) => {
      state.loading.getUsersBulkUploadViewReport = false;
      state.userBulkReportViewData = action.payload || null;
    });
    builder.addCase(getUsersBulkUploadViewReport.rejected, (state, action: any) => {
      state.loading.getUsersBulkUploadViewReport = false;
      state.error = action.payload || "Failed to fetch Users Bulk Report View";
    });

    builder.addCase(getUsersBulkUploadReport.pending, (state) => {
      state.loading.getUsersBulkUploadReport = true;
      state.error = null;
    });
    builder.addCase(getUsersBulkUploadReport.fulfilled, (state) => {
      state.loading.getUsersBulkUploadReport = false;
    });
    builder.addCase(getUsersBulkUploadReport.rejected, (state, action: any) => {
      state.loading.getUsersBulkUploadReport = false;
      state.error = action.payload || "Failed to fetch user bulk upload report";
    });

    builder.addCase(getReportDownloadFile.pending, (state) => {
      state.loading.getReportDownloadFile = true;
      state.error = null;
    });
    builder.addCase(getReportDownloadFile.fulfilled, (state) => {
      state.loading.getReportDownloadFile = false;
    });
    builder.addCase(getReportDownloadFile.rejected, (state, action: any) => {
      state.loading.getReportDownloadFile = false;
      state.error = action.payload || "Failed to export file";
    });
    builder.addCase(deleteInactiveUser.fulfilled, (state) => {
      state.loading.deleteInactiveUser = false;
    });
    builder.addCase(deleteInactiveUser.pending, (state) => {
      state.loading.deleteInactiveUser = true;
    });
    builder.addCase(deleteInactiveUser.rejected, (state) => {
      state.loading.deleteInactiveUser = false;
    });
    builder.addCase(updateUsersBulkUpload.pending, (state) => {
      state.loading.updateUsersBulkUpload = true;
    });
    builder.addCase(updateUsersBulkUpload.fulfilled, (state) => {
      state.loading.updateUsersBulkUpload = false;
    });
    builder.addCase(updateUsersBulkUpload.rejected, (state) => {
      state.loading.updateUsersBulkUpload = false;
    });
    builder.addCase(viewUser.pending, (state) => {
      state.loading.viewUser = true;
    });
    builder.addCase(viewUser.fulfilled, (state, action) => {
      state.loading.viewUser = false;
      state.userDetails = action.payload;
    });
    builder.addCase(viewUser.rejected, (state) => {
      state.loading.viewUser = false;
    });
    builder.addCase(getMyTeamList.pending, (state) => {
      state.loading.myTeamList = true;
    });
    builder.addCase(getMyTeamList.fulfilled, (state, action) => {
      state.loading.myTeamList = false;
      state.myTeamList = action.payload;
    });
    builder.addCase(getMyTeamList.rejected, (state) => {
      state.loading.myTeamList = false;
    });

    builder.addCase(getWorkingLocation.pending, (state) => {
      state.loading.workingLocation = true;
    });
    builder.addCase(getWorkingLocation.fulfilled, (state, action) => {
      state.loading.workingLocation = false;
      state.workingLocation = action.payload || [];
    });
    builder.addCase(getWorkingLocation.rejected, (state, action) => {
      state.loading.workingLocation = false;
      state.error = action.payload as string;
    });

    builder
      .addMatcher(
        isAnyOf(
          uploadUserImage.pending,
          deleteUserImage.pending
        ),
        (state) => {
          state.loading.uploadImageLoading = true;
        }
      )
      .addMatcher(
        isAnyOf(
          uploadUserImage.fulfilled,
          uploadUserImage.rejected,
          deleteUserImage.fulfilled,
          deleteUserImage.rejected
        ),
        (state) => {
          state.loading.uploadImageLoading = false;
        }
      )

      .addMatcher(
        isAnyOf(
          updateBasicInfo.pending,
          updateCurrentPosition.pending,
          updatePersonalInfo.pending,
          updateStatutoryInfo.pending,
          updateBankInfo.pending,
          updateBackgroundCheck.pending,
          updateJoiningInfo.pending,
          updatePresentAddress.pending,
          updateEmergencyContact.pending,
          updateEducationDetails.pending,
          updateEmployeeIdentity.pending
        ),
        (state) => {
          state.loading.updateLoading = true;
        }
      )

      .addMatcher(
        isAnyOf(
          updateBasicInfo.fulfilled,
          updateCurrentPosition.fulfilled,
          updatePersonalInfo.fulfilled,
          updateStatutoryInfo.fulfilled,
          updateBankInfo.fulfilled,
          updateBackgroundCheck.fulfilled,
          updateJoiningInfo.fulfilled,
          updatePresentAddress.fulfilled,
          updateEmergencyContact.fulfilled,
          updateEducationDetails.fulfilled,
          updateEmployeeIdentity.fulfilled
        ),
        (state) => {
          state.loading.updateLoading = true;
        }
      )

      .addMatcher(
        isAnyOf(
          updateBasicInfo.rejected,
          updateCurrentPosition.rejected,
          updatePersonalInfo.rejected,
          updateStatutoryInfo.rejected,
          updateBankInfo.rejected,
          updateBackgroundCheck.rejected,
          updateJoiningInfo.rejected,
          updatePresentAddress.rejected,
          updateEmergencyContact.rejected,
          updateEducationDetails.rejected,
          updateEmployeeIdentity.rejected
        ),
        (state, action) => {
          state.loading.updateLoading = false;
          state.error = action.payload as string;
        }
      );
  },
});
export default userSlice.reducer;
